/**
 * 백테스팅 API 클라이언트
 */
import {
  DrawdownPoint,
  EquityPoint,
  MonthlyReturn,
  PerformanceSource,
  TradeLogEntry,
} from '../types/performance';
import { API_BASE_URL } from '../config';
import { apiFetch } from '../utils/apiFetch';

export interface BacktestRequest {
  symbol: string;
  strategy: string;
  start_date: string;
  end_date: string;
  initial_capital?: number;
}

export interface BacktestResult {
  symbol: string;
  strategy: string;
  source: PerformanceSource;
  start_date: string;
  end_date: string;
  initial_capital: number;
  total_return: number;
  sharpe_ratio: number;
  max_drawdown: number;
  win_rate: number;
  total_trades: number;
  trades: TradeLogEntry[];
  trade_log: TradeLogEntry[];
  equity_curve: EquityPoint[];
  drawdown_series: DrawdownPoint[];
  monthly_returns: MonthlyReturn[];
}

export interface StrategyInfo {
  code: string;
  name: string;
}

export interface ComparisonResult {
  strategy: string;
  code: string;
  return: number;
  sharpe: number;
  win_rate: number;
  max_drawdown: number;
  trades: number;
}

export interface ComparisonResponse {
  symbol: string;
  period: string;
  start_date: string;
  end_date: string;
  best_strategy: {
    strategy: string;
    code: string;
    return: number;
    sharpe: number;
    win_rate: number;
  };
  results: ComparisonResult[];
}

/**
 * 백테스트 실행
 */
export async function runBacktest(
  request: BacktestRequest
): Promise<BacktestResult> {
  const response = await apiFetch(`${API_BASE_URL}/api/backtest/run`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || '백테스트 실행 실패');
  }

  const data = await response.json();
  return {
    ...data,
    source: 'backtest',
  };
}

/**
 * 전략 비교
 */
export async function compareStrategies(
  symbol: string,
  period: '3M' | '6M' | '1Y',
  strategies?: string[]
): Promise<ComparisonResponse> {
  const response = await apiFetch(`${API_BASE_URL}/api/backtest/comparison`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      symbol,
      period,
      strategies,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || '전략 비교 실패');
  }

  return response.json();
}

/**
 * 사용 가능한 전략 목록 조회
 */
export async function getAvailableStrategies(): Promise<StrategyInfo[]> {
  const response = await apiFetch(`${API_BASE_URL}/api/backtest/strategies`);

  if (!response.ok) {
    throw new Error('전략 목록 조회 실패');
  }

  const data = await response.json();
  return data.strategies;
}
